import { COMPONENT, ID } from "../constants";
import { GlossaryPluginComponent } from "../renderers/componentRenderers/glossary";
import { IframePluginComponent } from "../renderers/componentRenderers/Iframe";
import { PageBreakPluginComponent } from "../renderers/componentRenderers/pageBreak";
import { SppPluginComponent } from "../renderers/componentRenderers/spp/";
import { TdxPluginComponent } from "../renderers/componentRenderers/tdx";
import { CodeBlockPluginComponent } from "../renderers/componentRenderers/codeBlock/CodeBlockPluginComponent";
import { SingleSpaPluginComponent } from "../renderers/componentRenderers/singlespa";
import { JournalPluginComponent } from "../renderers/componentRenderers/journal";
import { EtextMcqPluginComponent } from "../renderers/componentRenderers/etextMcq/EtextMcqPluginComponent";
import { SpacerComponent } from "../renderers/componentRenderers/spacer/SpacerComponent";
import { DefaultComponentRenderer } from "../renderers/spf-renderers/default";
import { StageElement, ScreenConfig, type ThemeVariant, type ViewerDeviceRuntime } from "../types";
import { getElementHeightByLocalRef } from "./getElementHeight";
import type { Media, Link } from "@bronte/viewer";
import { RendererTypes } from "@bronte/viewer/src/main";
import "../index.scss";
/**
 * Get component type from plugin ID
 * @param pluginId - The plugin ID
 * @returns The component type or undefined
 */
export const getComponentType = (pluginId: string | undefined): string | undefined => {
  return pluginId ? ID[pluginId] : undefined;
};

/**
 * Render plugin component based on component type
 * @param element - The stage element
 * @param key - The React key
 * @param screenConfig - The screen configuration
 * @returns The rendered component or null
 */
export const renderPluginByType = (
  element: StageElement,
  key: string,
  screenConfig: ScreenConfig,
  getGlossaryData?: () => Promise<any[]>,
  getAuthToken?: (callback: (token: string) => void) => void,
  env?: string,
  onMediaClick?: (data: Media) => void,
  themeVariant?: Partial<ThemeVariant>,
  viewerDevice?: ViewerDeviceRuntime,
  onLinkClick?: (data: Link) => void,
  renderer?: RendererTypes,
): JSX.Element | null => {
  // Find component config by localRef
  const localRef = (element as any).localRef || element.ref;
  const componentConfig = screenConfig.components?.find((comp: any) => comp.localRef === localRef);
  const height = localRef ? getElementHeightByLocalRef(screenConfig, localRef) : undefined;

  if (!componentConfig) {
    return (
      <DefaultComponentRenderer element={element}>
        no component config found
      </DefaultComponentRenderer>
    );
  }

  // Get plugin ID from component config
  const pluginId = componentConfig.pluginPayload?.summary?.pluginId || componentConfig.$id;
  const componentType = getComponentType(pluginId);

  // Switch based on component type
  switch (componentType) {
    case COMPONENT.MMI:
    case COMPONENT.SPP:
      // Handle SPP plugin
      return (
        <SppPluginComponent
          key={key}
          element={element}
          componentConfig={componentConfig}
          componentType={componentType}
          env={env}
          viewerDevice={viewerDevice}
          onMediaClick={onMediaClick}
        />
      );
    case COMPONENT.TDX:
      return (
        <TdxPluginComponent
          key={key}
          element={element}
          componentConfig={componentConfig}
          getAuthToken={getAuthToken}
          env={env}
          themeVariant={themeVariant}
        />
      );
    case COMPONENT.IFRAME:
      return (
        <IframePluginComponent
          key={key}
          componentConfig={componentConfig}
          height={height}
          onLinkClick={onLinkClick}
          renderer={renderer}
          viewerDevice={viewerDevice}
        />
      );
    case COMPONENT.PAGEBREAK:
      return <PageBreakPluginComponent key={key} componentConfig={componentConfig} />;
    case COMPONENT.GLOSSARY:
      return getGlossaryData ? (
        <GlossaryPluginComponent
          key={key}
          componentConfig={componentConfig}
          getGlossaryData={getGlossaryData}
        />
      ) : (
        <DefaultComponentRenderer key={key} element={element}>
          Glossary data not available
        </DefaultComponentRenderer>
      );
    case COMPONENT.CODE:
      return (
        <CodeBlockPluginComponent key={key} element={element} componentConfig={componentConfig} />
      );
    case COMPONENT.SINGLESPA:
      return (
        <SingleSpaPluginComponent
          key={key}
          element={element}
          componentConfig={componentConfig}
          getAuthToken={getAuthToken}
          viewerDevice={viewerDevice}
        />
      );
    case COMPONENT.JOURNAL:
      return (
        <JournalPluginComponent
          key={key}
          element={element}
          componentConfig={componentConfig}
          getAuthToken={getAuthToken}
          env={env}
          viewerDevice={viewerDevice}
        />
      );
    case COMPONENT.ETEXT_MCQ:
      return (
        <EtextMcqPluginComponent
          key={key}
          element={element}
          componentConfig={componentConfig}
          onMediaClick={onMediaClick}
          themeVariant={themeVariant}
          viewerDevice={viewerDevice}
        />
      );
    case COMPONENT.SPACER:
      return <SpacerComponent key={key} height={height} componentConfig={componentConfig} />;
    default:
      // Default plugin handling
      return (
        <DefaultComponentRenderer>
          <div>
            {" "}
            {componentType} pluginId: {pluginId}
          </div>
        </DefaultComponentRenderer>
      );
  }
};
